import { SERVER_URL } from 'Constants';
import axios from 'axios';
import { buildUrl } from 'Util/FetchUtils';
import alertToast from 'Util/ToastifyUtils';
import { CheckInDto, EventBookingCheckInDto } from 'Models/Dtos/BookingDtos';
import { CargoTypeForCheckInDto } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/Types/CargoTypeForCheckInDto';

export const checkInBaseUrl = `${SERVER_URL}/api/check-in`;

/**
 * Creates new alteration for booking simply to update the associated ferry trip.
 * @param bookingId The ID of the booking to be updated.
 * @param ferryTripId The ID of the ferry trip to update booking.
 */
export async function checkInMove(bookingId: string, ferryTripId: string, startStopId: string = '') {
	// Returned (and awaited by callers) so failures propagate — the caller surfaces the backend's
	// error message. The previous try/catch was dead code (the un-awaited axios.post never threw
	// synchronously) and swallowed every failure into a generic toast.
	return axios.post(
		buildUrl(`${checkInBaseUrl}/move`, {
			bookingId,
			ferryTripId,
			startStopId,
		}),
	);
}

/**
 * Returns the routing path based on bookingId and user group.
 */
export async function checkInQrCode(qrCodeId: string) {
	return axios.get(buildUrl(`${checkInBaseUrl}`, { qrCodeId }));
}

export async function checkInBoarding(ferryTripId: string, qrCodeId: string, stopId: string) {
	return axios.get(buildUrl(`${checkInBaseUrl}/boarding`, { ferryTripId, qrCodeId, stopId }));
}

export async function eventCheckInBoarding(ferryTripId: string, qrCodeId: string) {
	return axios.get(buildUrl(`${checkInBaseUrl}/event-boarding`, { ferryTripId, qrCodeId }));
}

/**
 * Returns whether the booking check in status has successfully been changed or not
 * @param checkInDto - contains ferryTripId, routeId, bookingId, checkedInStatusId, bookingType
 * and whether the booking will be checkedIn or not
 */
export async function updateCheckedInStatus(checkInDto: CheckInDto) {
	return axios.post(`${checkInBaseUrl}/update-check-in-status`, checkInDto);
}

export async function updateEventBookingCheckedInStatus(checkInDto: EventBookingCheckInDto) {
	return axios.post(`${checkInBaseUrl}/update-event-check-in-status`, checkInDto);
}

/**
 * Gets the check-in data required to show check-in initial page load.
 * @param ferryTripId: the id of the ferry trip to fetch the data for
 */
export async function getCheckInData(ferryTripId: string) {
	try {
		// Awaited on purpose: without it the promise is returned before it can reject, so this catch
		// never fires and the real HTTP error escapes to the caller — which reported it as the generic
		// "Could not find ferry trip" with nothing useful in the console.
		const response = await axios.get(buildUrl(`${checkInBaseUrl}/get-check-in-data`, { ferryTripId }));
		return response;
	} catch (e: any) {
		// Logged, not toasted: the caller (FerryCheckInPage) shows the user-facing message, and this
		// runs twice under StrictMode — toasting here stacked three errors on one failed load.
		console.error('Failed to fetch check-in data', e);
		return null;
	}
}

/**
 * Gets the check-in data required to show check-in initial page load.
 * @param ferryTripId: the id of the ferry trip to fetch the data for
 */
export async function getEventCheckInData(eventId: string) {
	try {
		const response = axios.get(buildUrl(`${checkInBaseUrl}/get-event-check-in-data`, { eventId }));
		return response;
	} catch (e: any) {
		alertToast(e.toString(), 'error');
		return null;
	}
}

/**
 * Gets a booking for check-in for the view booking page/sidebar.
 * @param bookingId: the id of the booking to fetch the data for
 * @param includeFirstTimeOnRoute: have the server work out the NEW badge. Only for a booking the
 * check-in list holds no row for — otherwise copy the list's value, which costs nothing.
 */
export async function getBooking(bookingId: string, includeFirstTimeOnRoute = false) {
	const response = axios.get(
		buildUrl(`${checkInBaseUrl}/get-booking-by-id`, {
			bookingId,
			...(includeFirstTimeOnRoute ? { includeFirstTimeOnRoute: 'true' } : {}),
		}),
	);
	return response;
}

/**
 * Gets an eventbooking for check-in for the view booking page/sidebar.
 * @param bookingId: the id of the booking to fetch the data for
 */
export async function getEventBooking(bookingId: string) {
	const response = axios.get(buildUrl(`${checkInBaseUrl}/get-event-booking-by-id`, { bookingId }));
	return response;
}

export async function getEventBookingByQrCodeId(qrCodeId: string) {
	const response = axios.get(buildUrl(`${checkInBaseUrl}/get-event-booking-by-qrcode-id`, { qrCodeId }));
	return response;
}

export async function getCargoTypesForCheckIn() {
	return axios.get<CargoTypeForCheckInDto[]>(`${checkInBaseUrl}/cargo-types`).then(res => res.data);
}

/**
 * One seat from the preview: where it is, who it is for, and how to say it out loud.
 *
 * <p>The id matters as much as the label. The label is read to the passenger; the id is what goes
 * back with the check-in so the seat they were promised is the seat they get.</p>
 */
export interface CheckInSeatPreview {
	passengerId: string;
	seatId: string;
	label: string;
}

/**
 * The seats a booking will be given the moment it is checked in — without giving them.
 *
 * <p>For the confirmation screen, which is shown BEFORE the check-in happens and so cannot read the
 * seats off the booking: they do not exist yet. A preview only; nothing is held until the deckhand
 * confirms.</p>
 *
 * <p>Answers an empty list rather than throwing. The confirmation is perfectly usable without it —
 * it just says nothing about seats — and failing the screen a deckhand is working a queue from is
 * far worse than being quiet.</p>
 */
export async function fetchCheckInSeatPreview(
	bookingId: string,
	ferryTripId: string,
): Promise<CheckInSeatPreview[]> {
	try {
		const { data } = await axios.get(
			buildUrl(`${checkInBaseUrl}/seat-preview`, { bookingId, ferryTripId }),
		);
		return ((data?.seats ?? []) as Partial<CheckInSeatPreview>[])
			.filter(
				(seat): seat is CheckInSeatPreview =>
					!!seat.passengerId && !!seat.seatId && !!seat.label,
			);
	} catch {
		return [];
	}
}

/** A trip check-in can move on to — see `GetNextCheckInDepartureQueryHandler`. */
export interface NextCheckInDeparture {
	ferryTripId: string;
	/** The stop the trip departs from, as check-in's `stopId`. */
	stopId: string;
	departureDateTime: Date;
	name: string;
	routeName: string;
	ferryName: string;
	checkInFinalised: boolean;
	/** Passengers booked on the trip. */
	passengerCount: number;
	/** Bookings on the trip that bring a vehicle. */
	vehicleCount: number;
}

export interface NextCheckInDepartures {
	/** The next departure on the same route, from the same stop. */
	nextOnRoute: NextCheckInDeparture | null;
	/** The next trip the same vessel sails, on any route. */
	nextForVessel: NextCheckInDeparture | null;
}

const toNextCheckInDeparture = (x: any): NextCheckInDeparture | null =>
	x ? { ...x, departureDateTime: new Date(x.departureDateTime) } : null;

/**
 * The trips check-in offers once this one is done, both within 24 hours of its departure.
 * @param ferryTripId the trip being checked in
 * @param stopId the stop being checked in at; omitted for the trip's first stop
 */
export async function getNextCheckInDeparture(
	ferryTripId: string,
	stopId?: string,
	signal?: AbortSignal,
): Promise<NextCheckInDepartures> {
	const response = await axios.get(
		buildUrl(`${checkInBaseUrl}/next-departure`, stopId ? { ferryTripId, stopId } : { ferryTripId }),
		{ signal },
	);
	return {
		nextOnRoute: toNextCheckInDeparture(response.data?.nextOnRoute),
		nextForVessel: toNextCheckInDeparture(response.data?.nextForVessel),
	};
}

/** Where check-in opens a trip — its summary, at the stop it departs from. */
export const nextCheckInDepartureUrl = (departure: NextCheckInDeparture) =>
	`/check-in/${departure.ferryTripId}/summary?stopId=${departure.stopId}`;
